import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import Badge from "../../ui/Badge/Badge";
import translate from "../../../i18n/translate";
import { dataLanguage } from "../../../i18n/translateRequest";
import { useIsRtl } from "shared/hooks/ppHooks";
import "./hotcard.css";

// The Hot card shows everything up front - artwork, price, plan name and what
// the plan actually includes - rather than hiding the detail behind a "Details"
// accordion. Sellers pick by price and allowance, so both have to be visible
// while scanning.
//
// Text comes through dataLanguage, which reads the site language, so the plan
// name and description follow whatever the seller has selected.
const HotBundleCard = ({
  columnStyle,
  item,
  onTypeClick,
  index,
  isDarkTheme,
  moveCard,
  id,
  isSelected,
}) => {
  const ref = useRef(null);
  const isRTL = useIsRtl();

  const [{ handlerId }, drop] = useDrop({
    accept: "cards",
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(dragItem) {
      if (!ref.current) return;
      const dragIndex = dragItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveCard(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "cards",
    item: () => ({ id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  const title = dataLanguage(item, "ar_title", "en_title", "he_title");
  const description = dataLanguage(item, "ar_des", "en_des", "he_des");
  const isRenewable = item.renew === "True" || item.renew === "true";
  const price = item.amount ? parseFloat(item.amount) : 0;

  return (
    <div
      className={`${columnStyle} mt-3`}
      data-handler-id={handlerId}
      style={{ opacity: isDragging ? 0 : 1 }}
    >
      <div
        ref={ref}
        dir={isRTL ? "rtl" : "ltr"}
        className={[
          "hot-card",
          isDarkTheme ? "hot-card--dark" : "",
          isSelected ? "hot-card--selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="button"
        tabIndex={0}
        aria-pressed={Boolean(isSelected)}
        onClick={() => onTypeClick(item)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTypeClick(item);
          }
        }}
      >
        <div className="hot-card__shot">
          <img src={item.url} alt={title || ""} loading="lazy" />
          {isRenewable && <Badge text={translate("Renewable")} />}
        </div>

        <div className="hot-card__meta">
          <div className="hot-card__line">
            <span className="hot-card__price">
              {price}
              <span className="hot-card__cur">&#8362;</span>
            </span>
          </div>

          {title && <h3 className="hot-card__title">{title}</h3>}
          {description && <p className="hot-card__desc">{description}</p>}
        </div>
      </div>
    </div>
  );
};

export default HotBundleCard;
